/**
 * HR Engine - Campagnes d'Évaluation TDR
 * Gestion des campagnes conformément au Cahier des Charges
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Plus,
  Search,
  Calendar,
  Play,
  Pause,
  Archive,
  Eye,
  Trash2,
  Loader2,
  Target,
  Users,
  CheckCircle2,
  AlertTriangle,
  Clock,
  BarChart3,
  FileText,
  Building2,
  ClipboardCheck,
  ChevronRight,
  Star,
  Settings,
  Send,
  RefreshCw,
  BookOpen
} from 'lucide-react';

// Configuration des types de campagnes
const CAMPAIGN_TYPES = {
  annual: { 
    label: 'Évaluation annuelle', 
    color: 'bg-blue-100 text-blue-700',
    icon: Calendar,
    description: 'Évaluation complète de fin d\'année'
  },
  midterm: { 
    label: 'Évaluation mi-parcours', 
    color: 'bg-amber-100 text-amber-700',
    icon: Clock,
    description: 'Point d\'étape semestriel'
  },
  probation: { 
    label: 'Période d\'essai', 
    color: 'bg-purple-100 text-purple-700',
    icon: ClipboardCheck,
    description: 'Évaluation de fin de période d\'essai'
  }
};

// Configuration des statuts
const STATUS_CONFIG = {
  draft: { 
    label: 'Brouillon', 
    color: 'bg-slate-100 text-slate-700',
    dotColor: 'bg-slate-400',
    icon: FileText
  },
  active: { 
    label: 'En cours', 
    color: 'bg-green-100 text-green-700',
    dotColor: 'bg-green-500',
    icon: Play
  },
  closed: { 
    label: 'Terminée', 
    color: 'bg-blue-100 text-blue-700',
    dotColor: 'bg-blue-500',
    icon: Archive
  }
};

export default function EvaluationCampaignsTDRPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  
  // Filtres
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activating, setActivating] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    campaignType: 'annual',
    periodStart: '',
    periodEnd: '',
    deadlineSelfEval: '',
    deadlineManagerEval: '',
    deadlineHRValidation: '',
    targetDepartmentIds: [],
    targetEmployeeIds: [],
    competencyIds: [],
    objectivesWeightPercent: 60,
    competenciesWeightPercent: 40
  });
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, deptsRes, empsRes, compsRes] = await Promise.all([
        api.get('/evaluation-tdr/campaigns'),
        api.get(`/departments?companyId=${user?.companyId}`),
        api.get(`/employees?companyId=${user?.companyId}`),
        api.get('/competency-reference')
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setDepartments(deptsRes.data.departments || deptsRes.data || []);
      setEmployees((empsRes.data.employees || empsRes.data || []).filter(e => e.status === 'Actif'));
      setCompetencies(compsRes.data.competencies || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      
      // Validation
      if (!formData.name.trim()) {
        toast.error('Le nom de la campagne est requis');
        return;
      }
      
      if (!formData.periodStart || !formData.periodEnd) {
        toast.error('Les dates de période sont requises');
        return;
      }
      
      if (formData.objectivesWeightPercent + formData.competenciesWeightPercent !== 100) {
        toast.error('La somme des pondérations doit égaler 100%');
        return;
      }
      
      await api.post('/evaluation-tdr/campaigns', formData);
      toast.success('Campagne créée avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleActivate = async (campaignId) => {
    try {
      setActivating(campaignId);
      const response = await api.post(`/evaluation-tdr/campaigns/${campaignId}/activate`);
      toast.success(`Campagne activée ! ${response.data.formsCreated} formulaires créés.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActivating(null);
    }
  };

  const handleClose = async (campaignId) => {
    try {
      await api.post(`/evaluation-tdr/campaigns/${campaignId}/close`);
      toast.success('Campagne clôturée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
    }
  };

  const handleDelete = async () => {
    // TODO: Implement delete
    toast.info('Fonctionnalité à venir');
    setDeleteConfirm(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      campaignType: 'annual',
      periodStart: '',
      periodEnd: '',
      deadlineSelfEval: '',
      deadlineManagerEval: '',
      deadlineHRValidation: '',
      targetDepartmentIds: [],
      targetEmployeeIds: [],
      competencyIds: [],
      objectivesWeightPercent: 60,
      competenciesWeightPercent: 40
    });
    setStep(1);
  };

  // Filtrer les campagnes
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (typeFilter !== 'all' && c.campaignType !== typeFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return c.name.toLowerCase().includes(query);
      }
      return true;
    });
  }, [campaigns, statusFilter, typeFilter, searchQuery]);

  // Statistiques
  const stats = useMemo(() => {
    const drafts = campaigns.filter(c => c.status === 'draft').length;
    const active = campaigns.filter(c => c.status === 'active').length;
    const closed = campaigns.filter(c => c.status === 'closed').length;
    
    // Stats agrégées des campagnes actives
    let totalForms = 0;
    let selfCompleted = 0;
    let managerCompleted = 0;
    let hrValidated = 0;
    
    campaigns.filter(c => c.status === 'active').forEach(c => {
      const s = c.stats || {};
      totalForms += s.totalParticipants || 0;
      selfCompleted += s.selfEvalCompleted || 0;
      managerCompleted += s.managerEvalCompleted || 0;
      hrValidated += s.hrValidated || 0;
    });
    
    const avgProgress = totalForms > 0 ? Math.round((hrValidated / totalForms) * 100) : 0;
    
    return { drafts, active, closed, totalForms, selfCompleted, managerCompleted, hrValidated, avgProgress };
  }, [campaigns]);

  const toggleDepartment = (deptId) => {
    const current = formData.targetDepartmentIds;
    if (current.includes(deptId)) {
      setFormData({...formData, targetDepartmentIds: current.filter(id => id !== deptId)});
    } else {
      setFormData({...formData, targetDepartmentIds: [...current, deptId]});
    }
  };

  const toggleCompetency = (compId) => {
    const current = formData.competencyIds;
    if (current.includes(compId)) {
      setFormData({...formData, competencyIds: current.filter(id => id !== compId)});
    } else {
      setFormData({...formData, competencyIds: [...current, compId]});
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Campagnes d'Évaluation">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Campagnes d'Évaluation TDR">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-amber-500" />
              Campagnes d'Évaluation
            </h1>
            <p className="text-slate-500 mt-1">
              Module conforme au Cahier des Charges (TDR)
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" onClick={() => navigate('/competency-reference')}>
              <BookOpen className="w-4 h-4 mr-2" />
              Référentiel compétences
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle campagne
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <FileText className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                <div className="text-2xl font-bold text-slate-900">{stats.drafts}</div>
                <p className="text-sm text-slate-500">Brouillons</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Play className="w-6 h-6 mx-auto text-green-500 mb-2" />
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <p className="text-sm text-slate-500">En cours</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <Archive className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-blue-600">{stats.closed}</div>
                <p className="text-sm text-slate-500">Terminées</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="w-6 h-6 mx-auto text-indigo-500 mb-2" />
                <div className="text-2xl font-bold text-indigo-600">{stats.totalForms}</div>
                <p className="text-sm text-slate-500">Évaluations</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-500 mb-2" />
                <div className="text-2xl font-bold text-emerald-600">{stats.hrValidated}</div>
                <p className="text-sm text-slate-500">Validées RH</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <BarChart3 className="w-6 h-6 mx-auto text-purple-500 mb-2" />
                <div className="text-2xl font-bold text-purple-600">{stats.avgProgress}%</div>
                <p className="text-sm text-slate-500">Taux validation</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Rechercher une campagne..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="draft">Brouillon</SelectItem>
              <SelectItem value="active">En cours</SelectItem>
              <SelectItem value="closed">Terminée</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="annual">Évaluation annuelle</SelectItem>
              <SelectItem value="midterm">Mi-parcours</SelectItem>
              <SelectItem value="probation">Période d'essai</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Campaigns Grid */}
        {filteredCampaigns.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {campaigns.length === 0 ? 'Aucune campagne créée' : 'Aucune campagne trouvée'}
              </h3>
              <p className="text-slate-500 mb-4">
                {campaigns.length === 0 
                  ? 'Créez votre première campagne d\'évaluation TDR'
                  : 'Modifiez vos filtres pour voir plus de résultats'
                }
              </p>
              {campaigns.length === 0 && (
                <Button onClick={() => setShowCreateDialog(true)} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une campagne
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCampaigns.map((campaign) => {
              const type = CAMPAIGN_TYPES[campaign.campaignType] || CAMPAIGN_TYPES.annual;
              const status = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
              const TypeIcon = type.icon;
              const StatusIcon = status.icon;
              const s = campaign.stats || {};
              const progress = s.totalParticipants > 0 
                ? Math.round((s.hrValidated / s.totalParticipants) * 100) 
                : 0;
              
              return (
                <Card 
                  key={campaign.id} 
                  className="hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/evaluation-tdr/${campaign.id}`)}
                >
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color.replace('text-', 'bg-').split(' ')[0]}`}>
                          <TypeIcon className={`w-5 h-5 ${type.color.split(' ')[1]}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                          <Badge variant="outline" className={type.color}>
                            {type.label}
                          </Badge>
                        </div>
                      </div>
                      <Badge className={status.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>
                    
                    {/* Period */}
                    <div className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(campaign.periodStart).toLocaleDateString('fr-FR')} → {new Date(campaign.periodEnd).toLocaleDateString('fr-FR')}
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3 py-2 bg-slate-50 rounded-lg px-2">
                      <div className="text-center">
                        <p className="text-lg font-bold text-slate-900">{s.totalParticipants || 0}</p>
                        <p className="text-xs text-slate-500">Participants</p>
                      </div>
                      <div className="text-center border-l border-slate-200">
                        <p className="text-lg font-bold text-blue-600">{s.managerEvalCompleted || 0}</p>
                        <p className="text-xs text-slate-500">Éval. manager</p>
                      </div>
                      <div className="text-center border-l border-slate-200">
                        <p className="text-lg font-bold text-green-600">{s.hrValidated || 0}</p>
                        <p className="text-xs text-slate-500">Validées</p>
                      </div>
                    </div>
                    
                    {/* Progress */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Progression</span>
                        <span className="font-medium">{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      {campaign.status === 'draft' && (
                        <Button 
                          size="sm" 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActivate(campaign.id);
                          }}
                          disabled={activating === campaign.id}
                        >
                          {activating === campaign.id ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-1" />
                          )}
                          Activer
                        </Button>
                      )}
                      
                      {campaign.status === 'active' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClose(campaign.id);
                          }}
                        >
                          <Archive className="w-4 h-4 mr-1" />
                          Clôturer
                        </Button>
                      )}
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/evaluation-tdr/${campaign.id}`);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Détails
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Campaign Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            resetForm();
          }
        }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer une campagne d'évaluation</DialogTitle>
            </DialogHeader>
            
            {/* Steps indicator */}
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= s ? 'bg-amber-600 text-white' : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-amber-600' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>
            
            {/* Step 1: Informations générales */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900">Informations générales</h3>
                
                <div className="space-y-2">
                  <Label>Nom de la campagne *</Label>
                  <Input
                    placeholder="Ex: Évaluation annuelle 2025"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Description de la campagne..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Type de campagne</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(CAMPAIGN_TYPES).map(([key, type]) => {
                      const Icon = type.icon;
                      return (
                        <div
                          key={key}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            formData.campaignType === key 
                              ? 'border-amber-500 bg-amber-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setFormData({...formData, campaignType: key})}
                        >
                          <Icon className={`w-5 h-5 mb-2 ${formData.campaignType === key ? 'text-amber-600' : 'text-slate-400'}`} />
                          <p className="font-medium text-sm">{type.label}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Début de période évaluée *</Label>
                    <Input
                      type="date"
                      value={formData.periodStart}
                      onChange={(e) => setFormData({...formData, periodStart: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fin de période évaluée *</Label>
                    <Input
                      type="date"
                      value={formData.periodEnd}
                      onChange={(e) => setFormData({...formData, periodEnd: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Dates limites et pondérations */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900">Dates limites & Pondérations</h3>
                
                <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Échéances du workflow
                  </h4>
                  
                  <div className="grid gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center text-sm font-medium text-yellow-700">1</div>
                      <div className="flex-1">
                        <Label className="text-sm">Date limite auto-évaluation</Label>
                        <Input
                          type="date"
                          value={formData.deadlineSelfEval}
                          onChange={(e) => setFormData({...formData, deadlineSelfEval: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-700">2</div>
                      <div className="flex-1">
                        <Label className="text-sm">Date limite évaluation manager</Label>
                        <Input
                          type="date"
                          value={formData.deadlineManagerEval}
                          onChange={(e) => setFormData({...formData, deadlineManagerEval: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-sm font-medium text-green-700">3</div>
                      <div className="flex-1">
                        <Label className="text-sm">Date limite validation RH</Label>
                        <Input
                          type="date"
                          value={formData.deadlineHRValidation}
                          onChange={(e) => setFormData({...formData, deadlineHRValidation: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-medium text-amber-900 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Pondération de la note globale
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Objectifs (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={formData.objectivesWeightPercent}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setFormData({
                            ...formData, 
                            objectivesWeightPercent: val,
                            competenciesWeightPercent: 100 - val
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Compétences (%)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={formData.competenciesWeightPercent}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setFormData({
                            ...formData, 
                            competenciesWeightPercent: val,
                            objectivesWeightPercent: 100 - val
                          });
                        }}
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-amber-700">
                    Note finale = (Objectifs × {formData.objectivesWeightPercent}%) + (Compétences × {formData.competenciesWeightPercent}%)
                  </p>
                </div>
              </div>
            )}
            
            {/* Step 3: Cibles et compétences */}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="font-medium text-slate-900">Cibles & Compétences</h3>
                
                <Tabs defaultValue="departments">
                  <TabsList>
                    <TabsTrigger value="departments">Par département</TabsTrigger>
                    <TabsTrigger value="competencies">Compétences</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="departments" className="mt-4">
                    <p className="text-sm text-slate-500 mb-3">
                      Sélectionnez les départements à inclure. Si aucun n'est sélectionné, tous les employés actifs seront inclus.
                    </p>
                    
                    <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-2">
                      {departments.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">Aucun département</p>
                      ) : (
                        departments.map((dept) => (
                          <div
                            key={dept.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              formData.targetDepartmentIds.includes(dept.id)
                                ? 'bg-amber-50 border border-amber-200'
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => toggleDepartment(dept.id)}
                          >
                            <Checkbox checked={formData.targetDepartmentIds.includes(dept.id)} />
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{dept.name}</span>
                            <Badge variant="outline" className="ml-auto">
                              {employees.filter(e => e.departmentId === dept.id).length} employés
                            </Badge>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {formData.targetDepartmentIds.length > 0 && (
                      <p className="text-sm text-amber-600 mt-2">
                        {formData.targetDepartmentIds.length} département(s) sélectionné(s) - 
                        ~{employees.filter(e => formData.targetDepartmentIds.includes(e.departmentId)).length} employés
                      </p>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="competencies" className="mt-4">
                    <p className="text-sm text-slate-500 mb-3">
                      Sélectionnez les compétences à évaluer pour cette campagne.
                    </p>
                    
                    {competencies.length === 0 ? (
                      <Card className="py-8 text-center">
                        <CardContent>
                          <BookOpen className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500 mb-2">Aucune compétence dans le référentiel</p>
                          <Button variant="outline" size="sm" onClick={() => navigate('/competency-reference')}>
                            Créer des compétences
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-2">
                        {competencies.map((comp) => (
                          <div
                            key={comp.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              formData.competencyIds.includes(comp.id)
                                ? 'bg-purple-50 border border-purple-200'
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => toggleCompetency(comp.id)}
                          >
                            <Checkbox checked={formData.competencyIds.includes(comp.id)} />
                            <div className="flex-1">
                              <span className="font-medium">{comp.name}</span>
                              <Badge variant="outline" className="ml-2 text-xs">{comp.category}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {formData.competencyIds.length > 0 && (
                      <p className="text-sm text-purple-600 mt-2">
                        {formData.competencyIds.length} compétence(s) sélectionnée(s)
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  Précédent
                </Button>
              )}
              
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)} className="bg-amber-600 hover:bg-amber-700">
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={handleCreate} 
                  className="bg-green-600 hover:bg-green-700"
                  disabled={creating}
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Créer la campagne
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
